import { useEffect, useState, useMemo, useRef } from "react";
import FormControl from "../../components/common/FormControl";
import Select from "react-select";
import loader from "../../methods/loader";
import ApiClient from "../../methods/api/apiClient";
import environment from "../../environment";
import { useSelector } from "react-redux";
import { FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import pipeModel from "../../models/pipeModel";

export default function AddAttendee({
  id = "",
  eventId,
  guest = false,
  result = (e) => {},
  eventDetail,
  onDirtyChange = (dirty) => {},
  initialMembers = [],
}) {
  const user = useSelector((state) => state.user);
  const [submitted, setSubmitted] = useState(false);
  const [users, setUsers] = useState([]);
  const [members, setMember] = useState(
    initialMembers && initialMembers.length
      ? initialMembers
      : [{ fullName: "", email: "", firstName: "", lastName: "" }]
  );
  const [allMembersAdded, setAllMembersAdded] = useState(false);
  const [existingGuestCount, setExistingGuestCount] = useState(0);
  const [groupMemberCount, setGroupMemberCount] = useState(0);

  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [pendingSelections, setPendingSelections] = useState([]);

  // cw: Photo-list import (member mode) — resolve each seeded member against the loaded
  // dropdown list by email so the Member select is populated (and carries their email),
  // exactly like a manual selection. Runs once after the members list loads.
  const photoResolvedRef = useRef(false);
  useEffect(() => {
    if (guest || photoResolvedRef.current) return;
    if (!initialMembers || !initialMembers.length) return;
    if (!users.length) return;
    const resolved = initialMembers
      .map((im) => {
        const u = users.find((x) => (x.email || "").toLowerCase() === (im.email || "").toLowerCase());
        if (!u) return null;
        // members/list lowercases lastName, so prefer the preserved-case fullName for display
        const full = (u.fullName || "").trim();
        let firstName = u.firstName || "";
        let lastName = u.lastName || "";
        if (full) {
          const parts = full.split(/\s+/);
          firstName = parts[0] || firstName;
          lastName = parts.slice(1).join(" ") || lastName;
        }
        return { memberId: u.id, email: u.email, firstName, lastName };
      })
      .filter(Boolean);
    if (resolved.length) {
      setMember(resolved);
      photoResolvedRef.current = true;
    }
  }, [guest, users, initialMembers]);

  useEffect(() => {
    setAllMembersAdded(members.length === users.length);
  }, [members, users]);

  useEffect(() => {
    const hasContent = members.some(m => (m.email || '').trim() || (m.firstName || '').trim() || (m.lastName || '').trim() || m.memberId);
    onDirtyChange(hasContent);
  }, [members]);

  // cw: Fetch pending invitations for "Import Pending Invitations" button
  useEffect(() => {
    if (guest) {
      const groupId = eventDetail?.groupId?._id || "";
      if (groupId) {
        ApiClient.get("api/invitations/list", { groupId }).then(res => {
          if (res.success && res.data) {
            setPendingInvitations(res.data);
          }
        }).catch(() => {});
        ApiClient.get("api/members/list", { groupId }).then(res => {
          if (res.success) setGroupMemberCount(res.total || res.data?.length || 0);
        }).catch(() => {});
      }
    }
  }, [guest, eventDetail]);

  // cw: Import all pending invitations as guest fields (skip existing members)
  const handleImportPendingInvitations = () => {
    if (!pendingInvitations.length) {
      toast.warn("No pending invitations to import");
      return;
    }
    const memberEmails = users.map(u => u.email?.toLowerCase());
    const filtered = pendingInvitations.filter(inv => !memberEmails.includes(inv.email?.toLowerCase()));
    const skipped = pendingInvitations.length - filtered.length;
    if (!filtered.length) {
      toast.warn("All pending invitations are already group members");
      return;
    }
    const imported = filtered.map(inv => ({
      fullName: inv.fullName || "",
      email: inv.email || "",
      firstName: inv.firstName || "",
      lastName: inv.lastName || "",
    }));
    setMember(imported);
    let msg = `${imported.length} pending invitation${imported.length > 1 ? 's' : ''} imported. Review and click Save to invite as guests.`;
    if (skipped > 0) msg += ` (${skipped} skipped — already group members)`;
    toast.success(msg);
  };

  const [existingAttendeeEmails, setExistingAttendeeEmails] = useState([]);

  useEffect(() => {
    if (eventId) {
      ApiClient.get(`api/attendees/list`, { eventId, joinRequest: '' }).then((res) => {
        if (res.success && res.data) {
          setExistingAttendeeEmails(res.data.map((a) => (a.email || '').toLowerCase()));
          if (guest) {
            setExistingGuestCount(res.data.filter(a => a.attendeeRole === 'guest' || a.isGuest).length);
          }
        }
      });
    }
  }, [eventId]);

  const handleAddAllMembers = () => {
    let updatedMembers = users
      .filter((u) => !existingAttendeeEmails.includes(u.email?.toLowerCase()))
      .map((u) => ({
        memberId: u.id,
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
      }));
    setMember(updatedMembers);
    setAllMembersAdded(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    let method = "post";
    let url;
    let value;

    if (guest) {
      const maxGuests = Math.ceil(groupMemberCount / 2);
      const newGuestCount = members.filter(m => m.email && m.firstName).length;
      if (groupMemberCount > 0 && existingGuestCount + newGuestCount > maxGuests) {
        toast.error(`Guest limit reached. Maximum ${maxGuests} guests allowed (half of total group size: ${groupMemberCount}). ${existingGuestCount} already invited.`);
        return;
      }
      // cw: Handle multiple guests
      url = "api/add/guest/attendee";

      // cw: Send all guests, not just the first one
      const guestData = members.filter(m => m.email && m.firstName).map(m => ({
        eventId: eventId,
        groupId: eventDetail?.groupId?._id || "",
        email: m.email,
        firstName: pipeModel.trimText(m?.firstName),
        lastName: pipeModel.trimText(m?.lastName),
        fullName: pipeModel.trimText(m?.firstName) + " " + pipeModel.trimText(m?.lastName),
        addedBy: user?.id
      }));

      // Check if any guest emails belong to existing group members
      const memberEmails = users.map(u => u.email?.toLowerCase());
      const existingMembers = guestData.filter(g => memberEmails.includes(g.email?.toLowerCase()));
      if (existingMembers.length > 0) {
        const names = existingMembers.map(g => g.email).join(', ');
        toast.warn(`These emails already belong to group members: ${names}. Use "Invite Members" instead.`);
        return;
      }

      // cw: If only one guest, send single object; if multiple, send array
      if (guestData.length === 1) {
        value = guestData[0];
      } else {
        value = { data: guestData };
        url = "api/add/guest/attendees";  // cw: Use bulk endpoint for multiple guests
      }
    } else {
      url = "api/attendees";

      if (members.length === users.length) {
        value = {
          data: members.map((itm) => ({
            ...itm,
            eventId: eventId,
            attendeeRole: "member",
            addedBy: user._id,
            groupId: eventDetail?.groupId?._id || "",
            isConnectedMeating: true,
            firstName: pipeModel.trimText(itm?.firstName),
            lastName: pipeModel.trimText(itm?.lastName),
            fullName: pipeModel.trimText(itm.firstName) + " " + pipeModel.trimText(itm.lastName),
          })),
        };
      } else {
        value = {
          data: members
            .filter((itm) => itm.memberId)
            .map((itm) => ({
              ...itm,
              eventId: eventId,
              attendeeRole: "member",
              addedBy: user._id,
              groupId: eventDetail?.groupId?._id || "",
              isConnectedMeating: true,
              fullName: pipeModel.trimText(itm.firstName) + " " + pipeModel.trimText(itm.lastName),
            })),
        };
      }
    }

    loader(true);

    // cw: For multiple guests, loop through and send individually if bulk endpoint doesn't exist
    if (guest && members.filter(m => m.email && m.firstName).length > 1) {
      const guestData = members.filter(m => m.email && m.firstName).map(m => ({
        eventId: eventId,
        groupId: eventDetail?.groupId?._id || "",
        email: m.email,
        firstName: pipeModel.trimText(m?.firstName),
        lastName: pipeModel.trimText(m?.lastName),
        fullName: pipeModel.trimText(m?.firstName) + " " + pipeModel.trimText(m?.lastName),
        addedBy: user?.id
      }));

      // cw: Send guests one by one
      Promise.all(guestData.map(guest =>
        ApiClient.allApi("api/add/guest/attendee", guest, "post")
      )).then((results) => {
        const allSuccess = results.every(res => res.success);
        if (allSuccess) {
          result({ event: "submit", value: results });
          toast.success(`${guestData.length} guests invited successfully`);
        } else {
          toast.error("Some guests failed to be invited");
        }
        loader(false);
      }).catch(err => {
        toast.error("Failed to invite guests");
        loader(false);
      });
    } else {
      ApiClient.allApi(url, value, method).then((res) => {
        if (res.success) {
          result({ event: "submit", value: res });
          toast.success('Invitations emailed successfully');
        }
        loader(false);
      });
    }
  };

  const getUsers = (p = {}) => {
    let f = {
      ...p,
      groupId: eventDetail?.groupId?._id || "",
      eventId: eventId,
      isInvited: false,
      isDeleted: false,
    };
    ApiClient.get("api/members/list", f).then((res) => {
      if (res.success) {
        setUsers(res.data);
      }
    });
  };

  const getAttendeList = (p = {}) => {
    ApiClient.get(`api/attendees/list?eventId=${eventId}&joinRequest=`).then(
      (res) => {
        if (res.success && res.data) {
          const guestCount = res.data.filter(a => a.type === 'guest' && !a.isDeleted).length;
          setExistingGuestCount(guestCount);
        }
      }
    );
  };

  useEffect(() => {
    if (id) {
      loader(true);
      ApiClient.get("api/attendees/detail", { id }).then((res) => {
        if (res.success) {
          let value = res.data;
        }
        loader(false);
      });
    }
    getUsers();
    getAttendeList();
  }, [id]);

  const addMore = () => {
    if (guest && groupMemberCount > 0) {
      const maxGuests = Math.ceil(groupMemberCount / 2);
      if (existingGuestCount + members.length >= maxGuests) {
        toast.warn(`Guest limit: maximum ${maxGuests} guests allowed (half of total group size: ${groupMemberCount}). ${existingGuestCount} already invited.`);
        return;
      }
    }
    let arr = [...members];
    let payload = { fullName: "", email: "", lastName: "", firstName: "" };
    arr.unshift(payload);
    setMember(arr);
  };

  const updateMember = (i, key = "", value = "") => {
    let arr = members;
    arr[i][key] = value;
    setMember([...arr]);
  };

  const updateMemberAll = (i, values) => {
    let arr = members;
    arr[i] = {
      ...values,
      firstName: values.firstName || "",
      lastName: values.lastName || "",
    };
    setMember([...arr]);
  };

  const removeMember = (i) => {
    let arr = members;
    arr = arr.filter((itm, ind) => ind !== i);
    if (!guest && arr.length === 0) {
      arr = [{ fullName: "", email: "", firstName: "", lastName: "" }];
    }
    setMember([...arr]);
  };

  const memberOptions = useMemo(() => {
    const selectedEmails = members.filter(m => m.email).map(m => m.email.toLowerCase());
    const available = users.filter(u =>
      !selectedEmails.includes(u.email?.toLowerCase()) &&
      !existingAttendeeEmails.includes(u.email?.toLowerCase())
    );
    return [
      { value: 'all', label: 'All', data: null },
      ...available.map(u => ({
        value: u.id,
        label: `${u.fullName || `${u.firstName} ${u.lastName}`} (${u.email})`,
        data: u,
      })).sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
    ];
  }, [users, members, existingAttendeeEmails]);

  const commitPendingSelections = (rowIndex) => {
    if (pendingSelections.length === 0) return;
    if (pendingSelections.some(s => s.value === 'all')) {
      handleAddAllMembers();
      setPendingSelections([]);
      return;
    }
    const newRows = pendingSelections.map(s => ({
      memberId: s.data.id,
      email: s.data.email,
      firstName: s.data.firstName,
      lastName: s.data.lastName,
    }));
    let arr = [...members];
    arr.splice(rowIndex, 1, ...newRows);
    const seen = new Set();
    arr = arr.filter(m => {
      if (!m.memberId) return true;
      if (seen.has(m.memberId)) return false;
      seen.add(m.memberId);
      return true;
    });
    setMember(arr);
    setPendingSelections([]);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="">
        {members.map((itm, i) => (
          <div key={i} className="flex items-end gap-3 mt-4 pb-2">
            {guest ? (
              <div className="">
                <FormControl
                  type="text"
                  label="Email"
                  value={itm.email}
                  onChange={(e) => updateMember(i, "email", e)}
                  required
                />
              </div>
            ) : (
              <div className="flex-grow w-96">
                {!itm.memberId ? (
                  <div className="formWrapper relative">
                    <label className='text-sm'>Member <span className="star">*</span></label>
                    <Select
                      isMulti
                      options={memberOptions}
                      value={pendingSelections}
                      onChange={(selected) => setPendingSelections(selected || [])}
                      onMenuClose={() => commitPendingSelections(i)}
                      closeMenuOnSelect={false}
                      placeholder="Search by name or email"
                      className="text-lowercase text-gray-700 block text-sm options_classs"
                      formatOptionLabel={(opt) => opt.value === 'all' ? <span style={{fontWeight:700,color:'#EA580C'}}>{opt.label}</span> : opt.label}
                    />
                    {submitted && !itm.memberId && pendingSelections.length === 0 && (
                      <div className='text-danger small mt-1'>Member is required</div>
                    )}
                  </div>
                ) : (
                  <FormControl
                    type="select"
                    displayValue="displayLabel"
                    valueType="object"
                    placeholder="Search by name or email"
                    label="Member"
                    theme="search"
                    className="text-lowercase"
                    value={itm.memberId}
                    options={
                      users.length > 0
                        ? [
                            { id: "all", displayLabel: "All", email: "All" },
                            ...users.filter(
                              (u) =>
                                (!members.some(
                                  (m) => m.email?.toLowerCase() === u.email?.toLowerCase()
                                ) || itm.email?.toLowerCase() === u.email?.toLowerCase())
                            ).map(u => ({
                              ...u,
                              displayLabel: `${u.fullName || `${u.firstName} ${u.lastName}`} (${u.email})`
                            })).sort((a, b) => (a.firstName || a.fullName || '').toLowerCase().localeCompare((b.firstName || b.fullName || '').toLowerCase())),
                          ]
                        : []
                    }
                    onChange={(e) => {
                      if (e?.id === "all") {
                        handleAddAllMembers();
                      } else if (e?.id) {
                        updateMemberAll(i, {
                          ...itm,
                          memberId: e.id,
                          email: e.email,
                          firstName: e.firstName,
                          lastName: e.lastName,
                        });
                      } else {
                        updateMemberAll(i, {
                          ...itm,
                          memberId: "",
                          email: "",
                          firstName: "",
                          lastName: "",
                        });
                      }
                    }}
                    required={true}
                    error={submitted && !itm.memberId ? "Member is required" : ""}
                  />
                )}
              </div>
            )}
            <div className="">
              <label>First Name</label>
              <input
                value={itm.firstName}
                onChange={(e) => updateMember(i, "firstName", e.target.value)}
                required
                className="relative shadow-box bg-white w-full rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 capitalize"
                type="text"
                readOnly={!guest}
              />
            </div>
            <div className="">
              <label>Last Name</label>
              <input
                value={itm.lastName}
                onChange={(e) => updateMember(i, "lastName", e.target.value)}
                required
                className="relative shadow-box bg-white w-full rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 capitalize"
                type="text"
                readOnly={!guest}
              />
            </div>

            <div className="">
              {members.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeMember(i)}
                  className="text-black cursor-pointer flex items-center gap-2 focus:ring-0 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm py-2.5 text-center"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              ) : (
                ""
              )}
            </div>

            {i === 0 && (guest || (!guest && !allMembersAdded)) && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={addMore}
                  className="text-white bg-orange-600 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                  +
                </button>
              </div>
            )}
          </div>
        ))}

        <div className="flex justify-between items-center mt-3">
          <div>
            {guest && pendingInvitations.length > 0 && (
              <button
                type="button"
                onClick={handleImportPendingInvitations}
                className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              >
                Import Pending Invitations ({pendingInvitations.length})
              </button>
            )}
          </div>
          <button
            type="submit"
            className="text-white bg-orange-600 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
          >
            Send Invite
          </button>
        </div>
      </div>
    </form>
  );
}
